import { cn, TruncatedContent } from "@aurem/ui";
import { useNavigation } from "hooks/navigate";
import { ArrowLeftIcon, ChevronRightIcon } from "lucide-react";
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

type PageTitleContextValue = {
  breadcrumbs: BreadcrumbItem[];
  register: (id: number, items: BreadcrumbItem[]) => void;
  unregister: (id: number) => void;
};

type PageTitleProviderProps = {
  children?: ReactNode;
};

// Monotonically increasing counter — each useBreadcrumbs/usePageTitle call gets
// a unique ID on mount. The context always shows the highest-ID registration so
// that the deepest route component (mounted last → highest ID) wins over its
// ancestor layout components.
let nextRegistrationId = 0;

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export const PageTitleProvider = ({ children }: PageTitleProviderProps) => {
  const [registry, setRegistry] = useState<Map<number, BreadcrumbItem[]>>(
    new Map(),
  );

  const register = useCallback((id: number, items: BreadcrumbItem[]) => {
    setRegistry((prev) => new Map(prev).set(id, items));
  }, []);

  const unregister = useCallback((id: number) => {
    setRegistry((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const breadcrumbs = useMemo(() => {
    if (registry.size === 0) return [];
    const maxId = Math.max(...registry.keys());
    return registry.get(maxId) ?? [];
  }, [registry]);

  const value = useMemo(
    () => ({ breadcrumbs, register, unregister }),
    [breadcrumbs, register, unregister],
  );

  return <PageTitleContext value={value}>{children}</PageTitleContext>;
};

export function usePageTitle(title: string): void {
  const ctx = useContext(PageTitleContext);
  if (!ctx)
    throw new Error("usePageTitle must be used within PageTitleProvider");

  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = ++nextRegistrationId;

  useEffect(() => {
    ctx.register(idRef.current, title ? [{ label: title }] : []);
    return () => ctx.unregister(idRef.current);
  }, [title, ctx.register, ctx.unregister]);
}

export function useBreadcrumbs(items: BreadcrumbItem[]): void {
  const ctx = useContext(PageTitleContext);
  if (!ctx)
    throw new Error("useBreadcrumbs must be used within PageTitleProvider");

  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = ++nextRegistrationId;

  // JSON.stringify is intentional for deep equality — avoids requiring callers to useMemo their arrays.
  const stable = useMemo(() => items, [JSON.stringify(items)]);

  useEffect(() => {
    ctx.register(idRef.current, stable);
    return () => ctx.unregister(idRef.current);
  }, [stable, ctx.register, ctx.unregister]);
}

export const PageTitle = () => {
  const navigate = useNavigation();
  const ctx = useContext(PageTitleContext);
  if (!ctx) throw new Error("PageTitle must be used within PageTitleProvider");

  const { breadcrumbs } = ctx;

  if (breadcrumbs.length === 0) return null;

  const parentHref = breadcrumbs[breadcrumbs.length - 2]?.href;

  const handleBack = () => {
    navigate(parentHref!);
  };

  return (
    <div className="hidden sm:flex items-center gap-2">
      {parentHref && (
        <button
          onClick={handleBack}
          className="text-neutral-700 hover:text-neutral-900 transition-colors shrink-0"
          aria-label="Go back"
        >
          <ArrowLeftIcon size={18} className="rtl:-scale-x-100" />
        </button>
      )}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <Fragment key={index}>
              {index > 0 && (
                <ChevronRightIcon
                  size={16}
                  className="text-neutral-400 shrink-0 rtl:-scale-x-100"
                />
              )}
              {!isLast && item.href ? (
                <button
                  onClick={() => navigate(item.href!)}
                  className="text-md font-medium text-neutral-500 hover:text-neutral-700 transition-colors max-w-[160px] truncate"
                >
                  {item.label}
                </button>
              ) : (
                <TruncatedContent
                  as="span"
                  className={cn(
                    "text-md font-medium max-w-[200px]",
                    isLast ? "text-neutral-700" : "text-neutral-500",
                  )}
                >
                  {item.label}
                </TruncatedContent>
              )}
            </Fragment>
          );
        })}
      </nav>
    </div>
  );
};
